import React from 'react';
import ReactDatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import _ from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import styles from './datePicker.module.scss';
import classnames from 'classnames/bind';
import { Placement } from 'react-bootstrap/esm/Overlay';

export type DatePickerProps = {
  date: string;
  format?: string;
  className?: string;
  minDate?: string;
  maxDate?: string;
  minHour?: number;
  maxHour?: number;
  popOverClassName?: string;
  showTimePicker?: boolean;
  timeInterval?: 30 | 60;
  disabled: boolean;
  parseDate?: (date: string) => Date;
  onChange: (date: string | undefined) => void;
  formatDate?: (date: Date, locale?: string) => string;
  onPopOverClosed?: () => void;
  hasError: boolean;
  isEndTimePicker?: boolean;
  placement?: Placement;
};

export const defaultFormat = 'YYYY-MM-DD HH:mm:ss';
const ignoreTimeZoneFormat = 'YYYY-MM-DD HH:mm:ss';

export class DatePicker extends React.Component<DatePickerProps> {
  static defaultProps = {
    format: defaultFormat,
    onChange: _.noop,
    showTimePicker: true,
    timeInterval: 60,
    disabled: false,
    hasError: false,
    isEndTimePicker: false,
    placement: 'bottom'
  };
  cx: any;
  constructor(props) {
    super(props);
    this.cx = classnames.bind(styles);
  }

  // injectTimes must carry the selected day's date: react-datepicker pushes the
  // original injected Date object to onChange (it only rebases the displayed time),
  // so building these off `new Date()` would reset the date to today on click.
  buildInjectEndTimes = (base: Date) => {
    const endMinutes = this.props.timeInterval === 30 ? [29, 59] : [59];
    return _.flatten(
      Array.from({ length: 24 }, (_, hour) =>
        endMinutes.map(
          (minute) =>
            new Date(
              base.getFullYear(),
              base.getMonth(),
              base.getDate(),
              hour,
              minute
            )
        )
      )
    );
  };

  parseDate = (s: string) => {
    if (this.props.parseDate) {
      return this.props.parseDate(s);
    }
    return new Date(moment(s, this.props.format).format(ignoreTimeZoneFormat));
  };

  formatDate = (date: Date) => {
    if (this.props.formatDate) {
      return this.props.formatDate(date);
    }
    const clientTimeZone = moment.tz.guess();
    return moment.tz(date, clientTimeZone).format(this.props.format);
  };

  formatDateInternal = (date: Date) => {
    const clientTimeZone = moment.tz.guess();
    const format = this.props.showTimePicker
      ? 'YYYY-MM-DD HH:mm'
      : 'YYYY-MM-DD';
    return moment.tz(date, clientTimeZone).format(format);
  };

  handleChange = (date: Date) => {
    this.props.onChange(this.formatDate(date));
  };

  getDateWithinMinMax = (date: Date) => {
    let selectedDate = new Date(date);
    if (this.props.minHour && this.props.minHour > selectedDate.getHours()) {
      selectedDate.setHours(this.props.minHour);
    }
    if (this.props.maxHour && this.props.maxHour < selectedDate.getHours()) {
      selectedDate.setHours(this.props.maxHour);
    }
    const minDate = this.props.minDate
      ? this.parseDate(this.props.minDate)
      : undefined;
    const maxDate = this.props.maxDate
      ? this.parseDate(this.props.maxDate)
      : undefined;
    if (minDate && minDate.getTime() > selectedDate.getTime()) {
      selectedDate = minDate;
    }
    if (maxDate && maxDate.getTime() < selectedDate.getTime()) {
      selectedDate = maxDate;
    }
    return selectedDate;
  };

  onDateChange = (date: Date) => {
    const updateStartMoment = moment(this.getDateWithinMinMax(date)).startOf(
      'minute'
    );
    this.handleChange(updateStartMoment.toDate());
  };

  onClose = () => {
    this.props.onPopOverClosed && this.props.onPopOverClosed();
  };

  filterTime = (time) => {
    if (this.props.disabled) {
      return false;
    }

    const selectedDate = this.parseDate(this.props.date);
    const timeDate = new Date(time);
    selectedDate.setHours(timeDate.getHours());
    selectedDate.setMinutes(timeDate.getMinutes());

    const dateWithinMinMax = this.getDateWithinMinMax(selectedDate);
    if (dateWithinMinMax.getTime() !== selectedDate.getTime()) {
      return false;
    }

    return true;
  };

  render() {
    const {
      date: dateString,
      showTimePicker,
      timeInterval,
      minDate,
      maxDate,
      popOverClassName,
      className,
      hasError,
      disabled,
      isEndTimePicker,
      placement
    } = this.props;
    const date = this.parseDate(dateString);
    const timePickerProps = showTimePicker
      ? _.omitBy(
          {
            timeFormat: 'HH:mm',
            timeIntervals: isEndTimePicker ? 60 * 24 : timeInterval,
            showTimeSelect: true,
            filterTime: this.filterTime,
            injectTimes: isEndTimePicker
              ? this.buildInjectEndTimes(date)
              : undefined,
            excludeTimes: isEndTimePicker
              ? [moment(date).startOf('day').toDate()]
              : undefined,
            calendarClassName: isEndTimePicker ? 'end-date' : undefined
          },
          _.isNil
        )
      : undefined;
    const basicDatePickerProps = {
      date,
      disabledKeyboardNavigation: true,
      showMonthDropdown: true,
      showYearDropdown: true,
      inline: true
    };
    const dateMinMax = this.props.disabled
      ? {
          minDate: date,
          maxDate: moment(date).subtract(1, 'day').toDate()
        }
      : {
          minDate: minDate ? this.parseDate(minDate) : undefined,
          maxDate: maxDate ? this.parseDate(maxDate) : undefined
        };
    const inputClass = this.cx('datePickerInput', className, {
      error: hasError,
      disabled
    });
    return (
      <OverlayTrigger
        placement={placement}
        trigger='click'
        rootClose
        onExited={this.onClose}
        overlay={
          <Popover id='popover-basic' className={popOverClassName}>
            <Popover.Content className={styles.datePickerContainer}>
              <ReactDatePicker
                selected={date}
                onChange={this.onDateChange}
                {...basicDatePickerProps}
                {...dateMinMax}
                {...timePickerProps}
              />
            </Popover.Content>
          </Popover>
        }
      >
        <span className={`form-control ${inputClass}`}>
          {`${this.formatDateInternal(this.parseDate(dateString))}`}
        </span>
      </OverlayTrigger>
    );
  }
}
